import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getLdapSettingThunk } from 'thunks/setting/getLdapSettingThunk';
import { updateBlockSenderThunk } from 'thunks/setting/updateBlockedSenderThunk';
import normalizeFormErrors from 'utils/updateFormErrors';
import { SenderState } from 'slices/setting/approveSender';
import { mergeSenderControl } from '../../utils/mergeSenderControl';

interface SenderItemInterface {
  [key: string]: {
    value: string;
    isEmail: boolean;
    isDeleted: boolean;
    isNew: boolean;
  };
}

const initialState: SenderState = {
  draftSenderList: {},
  senderList: [],
  error: null,
};

export const blockSenderSlice = createSlice({
  name: 'blockSender',
  initialState,
  reducers: {
    updateDraftBlockSenderList: (state, action: PayloadAction<SenderItemInterface>): void => {
      state.draftSenderList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLdapSettingThunk.fulfilled, (state, action) => {
      if (action.payload.success) {
        state.senderList = action.payload.success.senderControl.blockedSenders;
      }
    });

    builder.addCase(updateBlockSenderThunk.fulfilled, (state, action) => {
      if (action.payload.error && !action.payload.fromInbox) {
        const error = normalizeFormErrors(action.payload.error);
        state.error = error.errorCode;
        return;
      }

      if (action.payload.success) {
        state.error = null;

        if (
          !action.payload.success?.added.domains.length &&
          !action.payload.success?.added.emails.length &&
          !action.payload.success?.removed.domains.length &&
          !action.payload.success?.removed.emails.length
        ) {
          const newDraft = { ...state.draftSenderList };

          Object.keys(newDraft).forEach((sender) => {
            if (state.draftSenderList[sender].isDeleted && state.draftSenderList[sender].isNew) {
              delete newDraft[sender];
            }
          });

          state.draftSenderList = newDraft;
          return;
        }

        const mergedAdded = mergeSenderControl(
          action.payload.success?.added?.emails,
          action.payload.success?.added?.domains
        );

        const mergedRemoved = mergeSenderControl(
          action.payload.success?.removed?.emails,
          action.payload.success?.removed?.domains
        );

        if (mergedAdded && mergedAdded.length > 0) {
          state.senderList = [...state.senderList, ...mergedAdded];
        }

        if (mergedRemoved && mergedRemoved.length > 0) {
          state.senderList = state.senderList.filter((item) => !mergedRemoved.includes(item));
        }

        state.draftSenderList = {};
      }
    });
  },
});

export const { updateDraftBlockSenderList } = blockSenderSlice.actions;

export default blockSenderSlice.reducer;
